export interface CsvPreviewResult {
  isValid: boolean;
  years: number[];
  months: { year: number; month: number; rowCount: number }[];
  totalRows: number;
  dateRange: { from: string; to: string } | null;
  warnings: string[];
  errors: string[];
}

/**
 * Parse CSV content and extract preview information
 * Analyzes Tauron eLicznik CSV format without full import
 */
export function previewCsvContent(csvContent: string): CsvPreviewResult {
  const result: CsvPreviewResult = {
    isValid: false,
    years: [],
    months: [],
    totalRows: 0,
    dateRange: null,
    warnings: [],
    errors: [],
  };

  try {
    // Split into lines and filter empty lines
    const lines = csvContent
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (lines.length === 0) {
      result.errors.push('Plik jest pusty');
      return result;
    }

    // Validate header (first line)
    const header = lines[0];
    const expectedColumns = [
      'Nr platnika',
      'Nr punktu poboru energii',
      'Rodzaj energii',
      'Data i godzina',
      'Strefa',
      'Wartosc[kWh/kvar]',
      'Kompletnosc[%]',
    ];

    const headerParts = header.split(';');

    // Check if header contains required columns
    const hasValidHeader = expectedColumns.every((col) =>
      headerParts.some((part) => part.includes(col))
    );

    if (!hasValidHeader) {
      result.errors.push(
        'Nieprawidłowy format pliku CSV. Upewnij się, że to plik eksportu z Tauron eLicznik.'
      );
      return result;
    }

    // Find date column index (should be "Data i godzina")
    const dateColumnIndex = headerParts.findIndex((col) =>
      col.toLowerCase().includes('data i godzina')
    );

    if (dateColumnIndex === -1) {
      result.errors.push('Nie znaleziono kolumny "Data i godzina"');
      return result;
    }

    // Count total data rows (excluding header)
    const dataLines = lines.slice(1);
    result.totalRows = dataLines.length;

    if (result.totalRows === 0) {
      result.errors.push('Plik nie zawiera danych (tylko nagłówek)');
      return result;
    }

    // Analyze first 100 lines for speed (but we already counted all rows)
    const linesToAnalyze = Math.min(100, dataLines.length);
    const dates: Date[] = [];
    const yearMonthCounts = new Map<string, number>();

    for (let i = 0; i < linesToAnalyze; i++) {
      const line = dataLines[i];
      const parts = line.split(';');

      if (parts.length <= dateColumnIndex) {
        continue; // Skip malformed line
      }

      const dateString = parts[dateColumnIndex].trim();

      // Expected format: "YYYY-MM-DD HH:MM" or "YYYY-MM-DD HH:MM:SS"
      const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);

      if (!dateMatch) {
        if (i < 5) {
          // Only report errors for first 5 rows to avoid spam
          result.warnings.push(`Wiersz ${i + 2}: nieprawidłowy format daty "${dateString}"`);
        }
        continue;
      }

      const [, year, month] = dateMatch;
      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(month, 10);

      // Validate year and month ranges
      if (yearNum < 2000 || yearNum > 2100) {
        result.warnings.push(`Wiersz ${i + 2}: podejrzany rok ${yearNum}`);
        continue;
      }

      if (monthNum < 1 || monthNum > 12) {
        result.warnings.push(`Wiersz ${i + 2}: nieprawidłowy miesiąc ${monthNum}`);
        continue;
      }

      // Parse full date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        dates.push(date);
      }

      // Count rows per year-month
      const yearMonth = `${year}-${month.padStart(2, '0')}`;
      yearMonthCounts.set(yearMonth, (yearMonthCounts.get(yearMonth) || 0) + 1);
    }

    if (dates.length === 0) {
      result.errors.push('Nie znaleziono żadnych prawidłowych dat w pliku');
      return result;
    }

    // Extract unique years
    const uniqueYears = new Set<number>();
    dates.forEach((date) => uniqueYears.add(date.getFullYear()));
    result.years = Array.from(uniqueYears).sort((a, b) => a - b);

    // Check for multiple years
    if (result.years.length > 1) {
      result.warnings.push(
        `Dane obejmują wiele lat: ${result.years.join(', ')}. Import zaimportuje wszystkie okresy.`
      );
    }

    // Build months breakdown
    const monthsBreakdown: { year: number; month: number; rowCount: number }[] = [];
    yearMonthCounts.forEach((count, yearMonth) => {
      const [year, month] = yearMonth.split('-');
      monthsBreakdown.push({
        year: parseInt(year, 10),
        month: parseInt(month, 10),
        rowCount: count,
      });
    });

    // Sort by year-month
    monthsBreakdown.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    result.months = monthsBreakdown;

    // Calculate date range
    const sortedDates = dates.sort((a, b) => a.getTime() - b.getTime());
    const firstDate = sortedDates[0];
    const lastDate = sortedDates[sortedDates.length - 1];

    result.dateRange = {
      from: firstDate.toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
      to: lastDate.toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    };

    // File is valid if we got this far with no critical errors
    result.isValid = result.errors.length === 0;
  } catch (err) {
    result.errors.push(
      `Błąd analizy pliku: ${err instanceof Error ? err.message : 'Nieznany błąd'}`
    );
    result.isValid = false;
  }

  return result;
}
